import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Colors from "../../ui/global/Colors";
import Fonts from "../../ui/global/Fonts";
import { useConfig } from "../../utils/config";
import ToolbarTop from "./components/ToolbarTop";
import InputSimple from "../../ui/components/InputSimple";
import Button from "../../ui/components/Button";
import AudioPlayer from "../../ui/components/AudioPlayer";
import Slider from "../../ui/components/Slider";
import LoadingSpinner from "../../ui/components/LoadingSpinner";
import { generateSound } from "../../ai/SoundAI";

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100svh;
    background-color: ${Colors.background};
    overflow: hidden;
    gap: 10px;
`;

const Inner = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 800px;
    min-height: 300px;
    max-height: 100%;
    margin: 54px 10px 10px 10px;
    gap: 10px;
`;

const Result = styled.div<{ $isGenerating: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: ${props => props.$isGenerating ? '200px' : 'auto'};
    gap: 10px;
`;

const GenerationControls = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 4px;
`;

const InputContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 16px;
    line-height: 1.2em;
    color: ${Colors.black};
    background-color: ${Colors.white};
    border: 1px solid ${Colors.grey1};
    border-radius: 8px;
    padding: 4px;

    &:hover {
        border: 1px solid ${Colors.grey2};
    }
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 10px;
`;

const Label = styled.div`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 12px;
  color: ${Colors.grey6};
  white-space: nowrap;
`;

const SliderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 200px;
`;

interface SoundProps {
    
}

const Sound = ({  }: SoundProps) => {
    const config = useConfig();
    const [description, setDescription] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [sound, setSound] = useState('');

    const generateAISound = async () => {
        try {
            setIsGenerating(true);
            
            if (config.DEBUG_DISABLE_SOUND_AI) { // Simulate sound generation
                // Use a promise with setTimeout to simulate async behavior
                await new Promise(resolve => {
                    setTimeout(() => {
                        setSound('https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3');
                        console.log('Sound generated (debug mode)');
                        resolve(null);
                    }, 3000);
                });
            } else { // Real sound generation
                const props = {
                    req: {
                        text: description,
                        duration_seconds: 1,
                    },
                    config,
                };
                console.log('Generating sound with', props);
                const result = await generateSound(props);
                setSound(result.url);
                console.log('Sound generated:', result.url);
            }
        } catch (error) {
            console.error('Error generating sound:', error);
            // Show error to user
            alert('Failed to generate sound. Please try again.');
        } finally {
            // Always set isGenerating to false when done, regardless of success or failure
            setIsGenerating(false);
        }
    };
    
    return (
        <Root>
            <ToolbarTop soundId={''} initialName={'...'} />
            <Inner>
                <Result $isGenerating={isGenerating}>
                    {sound && (
                        <AudioPlayer 
                            src={sound}
                            title="Demo Audio - Light Mode"
                            colorMode="light"
                        />
                    )}
                    {isGenerating && <LoadingSpinner />}
                </Result>

                <GenerationControls>
                    <InputContainer>
                        <InputSimple
                            placeholder="Create a sound effect..."
                            textAlign="left"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        <Button 
                            size="tiny" 
                            color="black" 
                            icon="send" 
                            onClick={generateAISound} 
                            disabled={description === ''}
                        />
                    </InputContainer>

                    {/* <Row>
                        <Label>Length</Label>
                        <SliderContainer>
                            <Slider 
                                showLabel={false}
                                size="small"
                                value={0}
                                min={0}
                                max={10}
                                onChange={(value) => {}}
                            />
                        </SliderContainer>
                    </Row> */}
                </GenerationControls>
            </Inner>
        </Root>
    );
};

export default Sound;
